/**
 * RBAC (Role-Based Access Control) Utilities
 * Centralized role hierarchy and permission checks
 *
 * Lower precedence number = more powerful role
 * Usage: userPrecedence <= ROLE_PRECEDENCE.admin (checks if user is admin or higher)
 */

export const ROLE_PRECEDENCE = {
  super_admin: 0,
  admin_manager: 10,
  admin: 15,
  taxlatesdata: 20,
  users: 100,
} as const;

export type UserRole = keyof typeof ROLE_PRECEDENCE;

/**
 * Get user's effective precedence from their Cognito groups
 * Returns the LOWEST precedence number (most powerful role)
 * @param groups - User's Cognito groups array
 * @returns Precedence number (lower = more powerful, 999 if no valid role)
 */
export function getUserPrecedence(groups: string[]): number {
  let lowestPrecedence = 999;

  for (const group of groups) {
    const precedence = ROLE_PRECEDENCE[group as UserRole];
    if (precedence !== undefined && precedence < lowestPrecedence) {
      lowestPrecedence = precedence;
    }
  }

  return lowestPrecedence;
}

/**
 * Check if user can assign JV submissions (admin_manager or higher: precedence <= 10)
 */
export function canAssignSubmissions(groups: string[]): boolean {
  return getUserPrecedence(groups) <= ROLE_PRECEDENCE.admin_manager;
}

/**
 * Check if user has admin access (admin or higher: precedence <= 15)
 */
export function hasAdminAccess(groups: string[]): boolean {
  return getUserPrecedence(groups) <= ROLE_PRECEDENCE.admin;
}

/**
 * Check if user is super admin (precedence <= 0)
 */
export function isSuperAdmin(groups: string[]): boolean {
  return getUserPrecedence(groups) <= ROLE_PRECEDENCE.super_admin;
}

/**
 * Check if user is admin manager (precedence <= 10)
 */
export function isAdminManager(groups: string[]): boolean {
  return getUserPrecedence(groups) <= ROLE_PRECEDENCE.admin_manager;
}

/**
 * Check if user can manage other admins (admin_manager or super_admin: precedence <= 10)
 */
export function canManageAdmins(groups: string[]): boolean {
  return getUserPrecedence(groups) <= ROLE_PRECEDENCE.admin_manager;
}

/**
 * Get user role display name
 */
export function getRoleDisplayName(groups: string[]): string {
  const precedence = getUserPrecedence(groups);

  if (precedence <= ROLE_PRECEDENCE.super_admin) return 'Super Administrator';
  if (precedence <= ROLE_PRECEDENCE.admin_manager) return 'Admin Manager';
  if (precedence <= ROLE_PRECEDENCE.admin) return 'Administrator';
  if (precedence <= ROLE_PRECEDENCE.taxlatesdata) return 'TaxLates Data Admin';
  return 'User';
}
